import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  apiSae,
  socketPath,
  urlWebSockt,
} from "../services/api";
import { io } from "socket.io-client";
import { Bounce, toast } from "react-toastify";
import { clearAuthToken, getAuthToken, useAuthToken } from "../services/authSession";
import { createGatewayStatusCache } from "../services/gatewayStatus";
import { applyStationEvent, createRefreshScheduler, createStationEventBuffer } from "../utils/stationRealtime";

export const HomeContext = createContext({});

export const HomeProvider = () => {
  const [modalAvisosEmegencia, setModalAvisosEmegencia] = useState(false);
  const [infoUser, setInfoUser] = useState({});
  const [infoEstacao, setInfoEstacao] = useState([]);
  const [userOn, setUserOn] = useState({});
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalDiagrama, setModalDiagrama] = useState(() => {
    const savedMode = localStorage.getItem("HomeMode");
    return savedMode === null || savedMode === "true";
  });
  const [mapaAtivo, setMapaAtivo] = useState(
    () => localStorage.getItem("EEEView") === "mapa"
  );
  const [statusPoco, setStatusPoco] = useState({});
  const [alerEmergenciaList, setAlerEmergenciaList] = useState([]);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("SaeDarkMode") === "true"
  );
  const [modalEditarSenha, setModalEditarSenha] = useState(false);

  const [modalBaixarApp, setModalBaixarApp] = useState(false);

  const currentUserIdRef = useRef(null);
  const statusPocosRequestIdRef = useRef(0);
  const stationRequestIdRef = useRef(0);
  const [realtimeBuffer] = useState(createStationEventBuffer);

  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthToken();
  const rotaUsaEstacoes =
    location.pathname.startsWith("/eee") ||
    location.pathname.startsWith("/mapa-geral");

  const headerApi = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  );

  const aoListarEstacoes = useCallback(async (idCidade, reload) => {
    const requestId = ++stationRequestIdRef.current;
    const revision = realtimeBuffer.revision;
    if (reload) {
      setLoading(true);
    }
    try {
      const listarEstacao = await apiSae.get(`estacao/leituratodos`, headerApi);

      const listaEstacaoTratada = listarEstacao.data.filter(
        (e) => String(e.cidade.id) === String(idCidade)
      );
      if (requestId === stationRequestIdRef.current) {
        setInfoEstacao(realtimeBuffer.replay(listaEstacaoTratada, revision));
      }
    } catch (error) {
      console.log(error);
      if (error.response?.status === 401) {
        let cofimationToken = getAuthToken();
        if (!cofimationToken) {
          navigate("/Login");
          if (
            window.ReactNativeWebView &&
            window.ReactNativeWebView.postMessage
          ) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({ type: "route", path: "Exit" })
            );
          }
        }
      }
    } finally {
      if (requestId === stationRequestIdRef.current) setLoading(false);
    }
  }, [headerApi, navigate, realtimeBuffer]);

  const gatewayCache = useMemo(() => createGatewayStatusCache(async (idGtw) => {
    const response = await apiSae.get(`/users/gtw/${idGtw}`, headerApi);
    return response.data;
  }), [headerApi]);
  const verificarStatusGtwPoco = useCallback((idGtw) => gatewayCache.get(idGtw), [gatewayCache]);

  useEffect(() => {
    if (!token || !rotaUsaEstacoes) return;
    aoListarEstacoes(1, true);
  }, [aoListarEstacoes, rotaUsaEstacoes, token]);

  const gatewaySignature = JSON.stringify(infoEstacao
      .filter(
        (estacao) =>
          estacao?.ativo === true &&
          Array.isArray(estacao?.pocos) &&
          estacao.pocos.length > 0
      )
      .map((estacao) => ({
        id: estacao.id,
        idGtw: estacao.pocos[0]?.gtw_poco,
      })));

  useEffect(() => {
    if (!rotaUsaEstacoes) return undefined;
    const estacoesAtivas = JSON.parse(gatewaySignature);
    const requestId = ++statusPocosRequestIdRef.current;

    const carregarStatuses = async () => {
      const statusEntries = await Promise.all(
        estacoesAtivas.map(async (estacao) => [
          estacao.id,
          await verificarStatusGtwPoco(estacao.idGtw),
        ])
      );

      if (requestId === statusPocosRequestIdRef.current) {
        setStatusPoco(Object.fromEntries(statusEntries));
      }
    };

    void carregarStatuses();
    return () => {
      statusPocosRequestIdRef.current += 1;
    };
  }, [gatewaySignature, rotaUsaEstacoes, userOn, verificarStatusGtwPoco]);

  useEffect(() => {
    if (!token) {
      sessionStorage.clear();
      navigate("/login");
    }
  }, [navigate, token]);

  useEffect(() => {
    if (!token) return undefined;

    const newSocket = io(urlWebSockt, {
      path: socketPath,
      auth: {
        token: token,
      },
      transports: ["websocket", "polling"],
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
    });

    setSocket(newSocket);
    const atualizarListaEstacoes = createRefreshScheduler(() => aoListarEstacoes(1, false));
    let connectedBefore = false;

    newSocket.on("connect", () => {
      console.log("WebSocket connected");
      toast.dismiss("sae-websocket-offline");
      if (connectedBefore) {
        gatewayCache.clear();
        setUserOn({ resync: Date.now() });
        atualizarListaEstacoes();
      }
      connectedBefore = true;
    });

    newSocket.on("user_data", (userData) => {
      setInfoUser(userData);
      currentUserIdRef.current = userData.id;
    });

    newSocket.on("user_updated", (userData) => {
      if (currentUserIdRef.current === userData?.id) {
        setInfoUser(userData);
      }
    });

    const atualizarEntidade = (event) => (payload) => {
      if (realtimeBuffer.record(event, payload)) {
        setInfoEstacao(current => applyStationEvent(current, event, payload));
      } else {
        atualizarListaEstacoes();
      }
    };
    const atualizarPoco = atualizarEntidade("update_elevatoria");
    const atualizarBomba = atualizarEntidade("update_Bomba");
    newSocket.on("update_elevatoria", atualizarPoco);
    newSocket.on("update_estacao", atualizarListaEstacoes);
    newSocket.on("update_Bomba", atualizarBomba);

    newSocket.on("changed_user", (userId) => {
      if (gatewayCache.updateUser(userId)) setUserOn(userId);
    });

    newSocket.on("User_desconectado_sessao", (userId) => {
      if (!currentUserIdRef.current || currentUserIdRef.current === userId) {
        clearAuthToken();
        navigate("/Login");

        toast.warn("Novo Dispositivo Conectado!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
          transition: Bounce,
        });

        if (
          window.ReactNativeWebView &&
          window.ReactNativeWebView.postMessage
        ) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: "route", path: "Exit" })
          );
        }
      }
    });

    newSocket.on("connect_error", (err) => {
      const tokenInvalido =
        ["Invalid token", "Missing token", "Session replaced", "Invalid token content"].includes(err.message) ||
        err.data?.message === "Invalid token";

      if (tokenInvalido) {
        clearAuthToken();
        if (
          window.ReactNativeWebView &&
          window.ReactNativeWebView.postMessage
        ) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: "route", path: "Exit" })
          );
        }
        navigate("/Login");
        toast.warn("Sessão expirada", {
          position: "top-right",
          autoClose: 5000,
          theme: "dark",
          transition: Bounce,
        });
        return;
      }

      toast.warn("Conexão em tempo real indisponível. Reconectando...", {
        toastId: "sae-websocket-offline",
        position: "top-right",
        autoClose: false,
        theme: "dark",
        transition: Bounce,
      });
    });

    return () => {
      currentUserIdRef.current = null;
      atualizarListaEstacoes.dispose();
      newSocket.off("update_elevatoria", atualizarPoco);
      newSocket.off("update_estacao", atualizarListaEstacoes);
      newSocket.off("update_Bomba", atualizarBomba);
      newSocket.disconnect();
    };
  }, [aoListarEstacoes, gatewayCache, navigate, realtimeBuffer, token]);

  return (
    <HomeContext.Provider
      value={{
        navigate,
        infoUser,
        infoEstacao,
        setInfoEstacao,
        headerApi,
        aoListarEstacoes,
        verificarStatusGtwPoco,
        socket,
        userOn,
        setUserOn,
        modalAvisosEmegencia,
        setModalAvisosEmegencia,
        modalBaixarApp,
        setModalBaixarApp,
        loading,
        modalEditarSenha,
        setModalEditarSenha,
        modalDiagrama,
        setModalDiagrama,
        mapaAtivo,
        setMapaAtivo,
        statusPoco,
        setStatusPoco,
        alerEmergenciaList,
        setAlerEmergenciaList,
        darkMode,
        setDarkMode,
      }}
    >
      <Outlet />
    </HomeContext.Provider>
  );
};
